import { Injectable } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiTodoService } from '@fi-app/fi-onboarding/fi-todo/fi-todo.service';
import { ITodoListItemBase } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { getObjectTypeNameTextId } from '@fi-shared/utils';

interface IBuildTodoActionsArgs<T> {
    onTodoDeletedFn: (item: T) => void;
}

@Injectable()
export class FiTodoActionsBuilderService {
    constructor(
        private appTextService: AppTextService,
        private fiStateService: FiStateService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiDialogsService: FiDialogsService,
        private fiTodoService: FiTodoService,
        private fiOnboardingHelperService: FiOnboardingHelperService,
    ) {}

    getTodoActions<T extends ITodoListItemBase>(args: IBuildTodoActionsArgs<T>): IItemsListAction2<T>[] {
        const hasShareRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.OnboardingLocal,
            AccessRightType.Share,
        );
        const todoListTextId = getObjectTypeNameTextId(ObjectTypes.TodoList);
        const todoListText = this.appTextService.get(todoListTextId).toLowerCase();

        const actions: IItemsListAction2<T>[] = [
            {
                title: this.appTextService.get('common.edit') + ' ' + todoListText,
                condition: (item) => hasShareRights && item.HasEditAccess,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-pen-to-square',
                onClick: (item) => this.fiStateService.go(FiStateNames.Onboarding.Todo.Edit._name, { id: item.Id }),
            },
            {
                title: this.appTextService.get('copy') + ' ' + todoListText,
                condition: (item) => hasShareRights,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-copy',
                onClick: (item) =>
                    this.fiStateService.go(FiStateNames.Onboarding.Todo.Add._name, { todoListId: item.Id }),
            },
            this.getDeleteAction(hasShareRights, todoListText, args.onTodoDeletedFn)
        ];

        return actions;
    }

    private getDeleteAction<T extends ITodoListItemBase>(
        hasShareRights: boolean,
        todoListText: string,
        onTodoDeletedFn: (item: T) => void,
    ): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('common.delete') + ' ' + todoListText,
            condition: (item) => hasShareRights && item.HasEditAccess,
            optionType: FiItemsListOptionType.Icon,
            iconClass: 'fa-regular fa-trash',
            onClick: (item) => {
                this.fiOnboardingHelperService.showStepElementDeletingConfirmationDialog({
                    stepElementObjectId: item.Id,
                    stepElementObjectType: ObjectTypes.TodoList,
                    stepElementName: item.Name,
                    deleteFn: () => {
                        this.fiTodoService.deleteTodoLists([item.Id]).then(() => {
                            onTodoDeletedFn(item);
                        });
                    },
                });
            },
        };
    }
}
